import * as yup from "yup";

import {
    Alert,
    Box,
    Container,
    FormControl,
    FormHelperText,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography
} from "@mui/material";
import {
    EagerLocation,
    LinkAction,
    LinkRegistry,
    Location
} from "../../models";
import {
    useMemo,
    useState
} from "react";

import { AlertMessage } from "../../lib/util/AlertMessage";
import { GraphQLLinkRegistryDAOFactory } from "../../lib/linkRegistry/dao/GraphQLLinkRegistryDAOFactory";
import GraphQLLocationDAOFactory from "../../lib/location/dao/graphql/GraphQLLocationDAOFactory";
import { GraphQLSearchLocationByPropertyIdOperation } from "../../lib/location/dao/GraphQLSearchLocationByPropertyIdOperation";
import { LinkRegistryDAO } from "../../lib/linkRegistry/dao/LinkRegistryDAO";
import LoadingButton from "@mui/lab/LoadingButton";
import LocationDAO from "../../lib/location/dao/LocationDAO";
import { PossibleDuplicateLocationsListItem } from "./PossibleDuplicateLocationsList";
import { PossibleDuplicateSelector } from "../../componentsV2/duplicateItems/PossibleDuplicateSelector";
import { ProposedNewLocation } from "./ProposedNewLocation";
import { StateContext } from "../../lib/design/document/state/StateContext";
import { VariablesForm } from "./VariablesForm";
import _ from "lodash";
import { checkForDuplicates } from "../../componentsV2/duplicateItems/CheckForDuplicate";
import { get } from "lodash";
import { linkRegistryFormSchema } from "../../lib/linkRegistry/schema/LinkRegistryFormSchema";
import theme from "../../assets/style/theme";
import { useCreateLocation } from "../../lib/location/hook/useCreateLocation";
import { useFormik } from "formik";

const locationDAO: LocationDAO & GraphQLSearchLocationByPropertyIdOperation = GraphQLLocationDAOFactory.getInstance();

type LinkRegistryFormProps = {
    id: string;
    linkRegistry: LinkRegistry | undefined;
    onSaved: () => (void | Promise<void>);
};

type LinkRegistryFormData = yup.InferType<typeof linkRegistryFormSchema>;

const linkRegistryDAO: LinkRegistryDAO = GraphQLLinkRegistryDAOFactory.getInstance();

export const LinkRegistryForm = (props: LinkRegistryFormProps) => {
    const { id, linkRegistry, onSaved } = props;
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [alertMessage, setAlertMessage] = useState<AlertMessage | undefined>();
    const [formState, setFormState] = useState<LinkRegistryFormData>();
    const { createLocation } = useCreateLocation(StateContext.INSPECTION);
    const [possibleDuplicates, setPossibleDuplicates] = useState<EagerLocation[]>();

    const initialValues = useMemo<LinkRegistryFormData>(() => ({
        linkId: id,
        linkAction: (linkRegistry?.action as LinkAction) || LinkAction.ASSESSMENT_FORM,
        variables: linkRegistry?.variables ? JSON.parse(linkRegistry.variables) : {},
        isPublished: linkRegistry?.published ?? false,
        shouldCreateNewLocation: false,
    }), [linkRegistry]);

    const onInitialFormComplete = async (data: LinkRegistryFormData) => {
        if (!get(data, 'shouldCreateNewLocation', false)) {
            submitData(data, false);
            return;
        }

        const duplicates = await checkForDuplicates({
            matchers: [
                { targetKey: "name", value: get(data.variables, "locationName", "") },
            ],
            fetchAllItems: () => locationDAO.getLocationsByPropertyId(
                get(data.variables, "propertyId", "")
            ),
            itemType: "location",
        });

        if (duplicates && duplicates.length > 0) {
            setFormState(data);
            setPossibleDuplicates(duplicates);
        } else {
            submitData(data, true);
        }
    };

    const submitData = async (data: LinkRegistryFormData, isNewLocation: boolean) => {
        try {
            setAlertMessage(undefined);
            if (isNewLocation) {
                const location = await createLocation(
                    new Location({
                        propertyId: get(data.variables, 'propertyId', undefined),
                        name: get(data.variables, 'locationName', ''),
                    }),
                    false
                );
                data = { ...data, variables: { ...data.variables, locationId: location.id } };
                await formik.setFieldValue("variables.locationId", location.id);
            }
            if (linkRegistry) {
                await linkRegistryDAO.update({
                    id: id,
                    propertyId: get(data.variables, 'propertyId', undefined),
                    action: data.linkAction,
                    variables: JSON.stringify(data.variables),
                    published: data.isPublished
                });
                setAlertMessage({
                    severity: "success",
                    message: "Links configured successfully"
                });
                await onSaved();
                return;
            }
            await linkRegistryDAO.create({
                id: id,
                propertyId: get(data.variables, 'propertyId', undefined),
                action: data.linkAction,
                variables: JSON.stringify(data.variables),
                published: data.isPublished
            });
            setAlertMessage({
                severity: "success",
                message: "Links configured successfully"
            });
            await onSaved();
        } catch (error) {
            setAlertMessage({
                severity: "error",
                message: `Failed to create link configuration: ${(error as Error).message}`
            });
        } finally {
            setIsSubmitting(false);
        }
    }

    const formik = useFormik<LinkRegistryFormData>({
        initialValues: initialValues,
        validationSchema: linkRegistryFormSchema,
        onSubmit: onInitialFormComplete
    });

    const useNewLocation = async () => {
        if (formState) {
            submitData(formState, true);
        }
        await formik.setFieldValue("shouldCreateNewLocation", false);
        setPossibleDuplicates(undefined);
    };

    const useExistingLocation = async (location: Location) => {
        await formik.setFieldValue(
            "variables.locationName",
            location.name
        );
        await formik.setFieldValue("variables.locationId", location.id);
        await formik.setFieldValue("shouldCreateNewLocation", false);

        if (formState) {
            const newData = {
                ...formState.variables,
                locationId: location.id,
                locationName: location.name,
            };
            const newState = { ...formState, shouldCreateNewLocation: false, variables: newData };
            submitData(newState, false);
        }
        setPossibleDuplicates(undefined);
    };

    const hasFormChanged = useMemo(() => {
        const areEqual = _.isEqual(formik.values, initialValues);
        return !areEqual;
    }, [formik.values, (formik.values.variables as any).locationId, initialValues]);


    const showDuplicateDialog = useMemo(() => {
        const isNewLocation = get(formik.values, "shouldCreateNewLocation", false);
        return isNewLocation && possibleDuplicates && possibleDuplicates?.length > 0;
    }, [formik.values, possibleDuplicates]);

    return (
        <Container component="main">
            <Box my={2} display="flex" flexDirection="column" alignItems="center">
                {!showDuplicateDialog ?
                    (<Box
                        component="form"
                        onSubmit={formik.handleSubmit}
                        noValidate
                        sx={{ mt: 1 }}
                        width="100%"
                        maxWidth={theme.breakpoints.values["md"]}
                    >
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextField
                                    required
                                    fullWidth
                                    id="linkId"
                                    label="Link ID"
                                    name="linkId"
                                    value={id}
                                    disabled
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <FormControl fullWidth error={formik.touched.linkAction && Boolean(formik.errors.linkAction)}>
                                    <InputLabel id="linkAction-label">Link Type</InputLabel>
                                    <Select
                                        id="linkAction"
                                        labelId="linkAction-label"
                                        label="Link Type"
                                        name="linkAction"
                                        value={formik.values.linkAction}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        required
                                    >
                                        <MenuItem value={LinkAction.ASSESSMENT_FORM}>Assessment Form</MenuItem>
                                        <MenuItem value={LinkAction.CUSTOM_LINK}>Custom Link</MenuItem>
                                    </Select>
                                    <FormHelperText>
                                        {formik.touched.linkAction && formik.errors.linkAction}
                                    </FormHelperText>
                                </FormControl>
                            </Grid>
                            <Grid
                                container
                                item
                                xs={12}
                                spacing={2}
                                alignContent="flex-start"
                            >
                                <VariablesForm formik={formik} />
                            </Grid>
                            {alertMessage && (
                                <Grid item xs={12}>
                                    <Alert severity={alertMessage.severity}>
                                        {alertMessage.message}
                                    </Alert>
                                </Grid>
                            )}
                            <Grid item xs={12}>
                                <Typography variant="h6">Publishing</Typography>
                                <Typography variant="body2">
                                    (If link is not enabled, scanning the QR code will link to
                                    this page for further configuration.)
                                </Typography>
                                <FormControl
                                    fullWidth
                                    error={
                                        formik.touched.isPublished &&
                                        Boolean(formik.errors.isPublished)
                                    }
                                    sx={{ mt: 1 }}
                                >
                                    <InputLabel id="isPublished-label">
                                        Publish Link?
                                    </InputLabel>
                                    <Select
                                        id="isPublished"
                                        labelId="isPublished-label"
                                        label="Publish Link"
                                        name="isPublished"
                                        value={
                                            formik.values.isPublished === true
                                                ? "yes"
                                                : formik.values.isPublished === false
                                                    ? "no"
                                                    : ""
                                        }
                                        onChange={(event) => {
                                            const value = event.target.value;
                                            formik.setFieldValue(
                                                "isPublished",
                                                value === "yes" ? true : value === "no" ? false : ""
                                            );
                                        }}
                                        onBlur={formik.handleBlur}
                                        required
                                    >
                                        <MenuItem value="yes">Yes</MenuItem>
                                        <MenuItem value="no">No</MenuItem>
                                    </Select>
                                    <FormHelperText>
                                        {formik.touched.isPublished && formik.errors.isPublished}
                                    </FormHelperText>
                                </FormControl>
                                <FormHelperText>
                                    {formik.touched.isPublished && formik.errors.isPublished}
                                </FormHelperText>
                            </Grid>
                            <Grid item xs={12}>
                                <LoadingButton
                                    type="submit"
                                    fullWidth
                                    variant="contained"
                                    loading={isSubmitting}
                                    startIcon={<></>}
                                    loadingPosition="start"
                                    disabled={!formik.isValid || !hasFormChanged}
                                >
                                    Submit
                                </LoadingButton>
                            </Grid>
                        </Grid>
                    </Box>) :
                    (<PossibleDuplicateSelector<Location>
                        onUseExisting={useExistingLocation}
                        possibleDuplicates={possibleDuplicates ?? []}
                        renderPossibleDuplicateItem={(item, isCurrentSelection, onClick, key) => (
                            <PossibleDuplicateLocationsListItem key={key} item={item} isCurrentSelection={isCurrentSelection} onClick={onClick} />
                        )}
                        proposedNewItem={
                            <ProposedNewLocation
                                locationName={get(formik.values.variables, "locationName", "")}
                                returnToEditing={() => {
                                    setPossibleDuplicates(undefined);
                                    setAlertMessage(undefined);
                                }}
                                useNewLocation={useNewLocation}
                            />
                        }
                        itemTypeDisplayString="location"
                    />
                    )}
            </Box>
        </Container>
    );
};
